import { useMemo } from 'react';
import { useAuthContext } from './use-auth-context';

/**
 * Hook to check user permissions
 * Works only for CRM users with menu permissions
 *
 * @returns {Object} Permission checking utilities
 */
export function usePermissions() {
  const { user } = useAuthContext();

  // Get user's menu with permissions
  const userMenu = user?.menu || [];

  /**
   * Check if user has a specific permission
   * @param {string} permissionName - The permission name to check
   * @param {string} accessType - 'read', 'write', or 'both'
   * @returns {boolean} - Whether user has the permission
   */
  const hasPermission = useMemo(() => {
    return (permissionName, accessType = 'read') => {
      // If no user or no menu, assume no permissions (unless not a CRM user)
      if (!user) {
        return false;
      }

      if (!userMenu || userMenu.length === 0) {
        // Not a CRM user - allow access
        return true;
      }

      // Find the menu item with this permission
      const menuItem = userMenu.find(item => item.permission_name === permissionName);

      if (!menuItem) {
        return false;
      }

      // Check access type
      let hasAccess = false;
      if (accessType === 'read') {
        hasAccess = menuItem.read_access === true || menuItem.read_access === 1;
      } else if (accessType === 'write') {
        hasAccess = menuItem.write_access === true || menuItem.write_access === 1;
      } else if (accessType === 'both') {
        const hasRead = menuItem.read_access === true || menuItem.read_access === 1;
        const hasWrite = menuItem.write_access === true || menuItem.write_access === 1;
        hasAccess = hasRead && hasWrite;
      }

    
      return hasAccess;
    };
  }, [user, userMenu]);

  /**
   * Check if user can access a specific path
   * @param {string} path - The path to check (e.g., '/dashboard/user')
   * @param {string} accessType - 'read', 'write', or 'both'
   * @returns {boolean} - Whether user can access the path
   */
  const canAccessPath = useMemo(() => {
    return (path, accessType = 'read') => {
      // If no user, no access
      if (!user) return false;

      // If no menu, assume not a CRM user - allow access
      if (!userMenu || userMenu.length === 0) return true;

      // Find menu item matching the path
      const menuItem = userMenu.find(item => {
        if (!item.path) return false;

        // Exact match
        if (item.path === path) return true;

        // Parent path match (e.g., '/dashboard/user' matches '/dashboard/user/new')
        if (path.startsWith(item.path + '/')) return true;

        return false;
      });

      if (!menuItem) {
        return false;
      }

      // Check access type
      if (accessType === 'read') {
        return menuItem.read_access === true || menuItem.read_access === 1;
      } else if (accessType === 'write') {
        return menuItem.write_access === true || menuItem.write_access === 1;
      } else if (accessType === 'both') {
        const hasRead = menuItem.read_access === true || menuItem.read_access === 1;
        const hasWrite = menuItem.write_access === true || menuItem.write_access === 1;
        return hasRead && hasWrite;
      }

      return false;
    };
  }, [user, userMenu]);

  /**
   * Get permission details for a specific permission
   * @param {string} permissionName - The permission name
   * @returns {Object|null} - Permission details with read_access and write_access
   */
  const getPermission = useMemo(() => {
    return (permissionName) => {
      if (!userMenu || userMenu.length === 0) return null;

      const menuItem = userMenu.find(item => item.permission_name === permissionName);

      if (!menuItem) return null;

      return {
        permission_name: menuItem.permission_name,
        read_access: menuItem.read_access === true || menuItem.read_access === 1,
        write_access: menuItem.write_access === true || menuItem.write_access === 1,
        path: menuItem.path,
        title: menuItem.title
      };
    };
  }, [userMenu]);

  /**
   * Check if user is a CRM user (has menu permissions)
   * Client and agency users bypass permission guards — the server handles their data filtering.
   * @returns {boolean}
   */
  const isCRMUser = useMemo(() => {
    if (user?.isClient) return false;
    return userMenu && userMenu.length > 0;
  }, [userMenu, user]);

  return {
    hasPermission,
    canAccessPath,
    getPermission,
    isCRMUser,
    userMenu
  };
}
